/**
 * OmegaBudgetPage — accounting / supplier-spend page (S45, /omega/budget).
 *
 * Every supplier's monthly spend, total spend vs the A-02 monthly ceiling,
 * per-supplier expected-range colour + alerts, and a manual "add supplier" form.
 * Reads the existing founder-gated budget endpoints (S49 Wave D):
 *   GET  /admin/budget/summary   — spent vs ceiling + by_supplier spend
 *   GET  /admin/budget/suppliers — registry (expected ranges, added_by)
 *   POST /admin/budget/suppliers — manual add (auto-detect happens server-side)
 *
 * Founder-only, English-only, white theme, OMEGA sidebar. Pure helpers exported
 * for tests.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';
import { OmegaSidebar, SIDEBAR_WIDTH, useIsMobile } from '@/features/omega/cockpit/OmegaSidebar';
import { STATUS_COLORS, type CardStatus } from '@/features/omega/cockpit/cardStatus';

// ── Types (mirror the Rust wire structs) ─────────────────────────────────────

interface SpendByKey {
  key: string;
  total_usd_cents: number;
}

interface BudgetSummary {
  spent_month_usd_cents: number;
  ceiling_month_usd_cents: number;
  remaining_usd_cents: number;
  degradation: string;
  by_supplier: SpendByKey[];
}

interface SupplierRow {
  supplier: string;
  category: string;
  expected_min_usd_cents: number;
  expected_max_usd_cents: number;
  added_by: string;
  first_seen: string;
}

interface DayCents {
  date: string;
  total_usd_cents: number;
}

// ── Pure helpers (exported for tests) ─────────────────────────────────────────

/** Format USD cents as "$X.XX". */
export function fmtUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Colour a supplier's spend against its expected range: red when over the max,
 * yellow when below an explicit min (under-running can also signal a problem),
 * green within range. An unset max (0) is treated as "no ceiling" → green.
 */
export function supplierStatus(spend: number, expectedMin: number, expectedMax: number): CardStatus {
  if (expectedMax > 0 && spend > expectedMax) return 'red';
  if (expectedMin > 0 && spend < expectedMin) return 'yellow';
  return 'green';
}

/** Overall status from the degradation level (matches the cockpit Budget card). */
export function ceilingStatus(degradation: string): CardStatus {
  if (degradation === 'hard_stop') return 'red';
  if (degradation === 'degraded') return 'yellow';
  return 'green';
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaBudgetPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;
  const isMobile = useIsMobile();
  const qc = useQueryClient();

  const [period, setPeriod] = useState<'month' | 'day'>('month');

  const summary = useQuery({
    queryKey: ['budget', 'summary'],
    queryFn: () => apiFetch<BudgetSummary>('/admin/budget/summary'),
    refetchInterval: 30_000,
  });
  const suppliers = useQuery({
    queryKey: ['budget', 'suppliers'],
    queryFn: () => apiFetch<{ suppliers: SupplierRow[] }>('/admin/budget/suppliers'),
    refetchInterval: 30_000,
  });
  const daily = useQuery({
    queryKey: ['budget', 'daily'],
    queryFn: () =>
      apiFetch<{ spent_today_usd_cents: number; by_supplier: SpendByKey[] }>('/admin/budget/daily'),
    refetchInterval: 30_000,
    enabled: period === 'day',
  });
  const [openHistory, setOpenHistory] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');

  const addSupplier = useMutation({
    mutationFn: (body: { supplier: string; category: string; amount_usd_cents: number }) =>
      apiFetch('/admin/budget/suppliers', { method: 'POST', body }),
    onSuccess: () => {
      setName('');
      setCategory('');
      setAmount('');
      void qc.invalidateQueries({ queryKey: ['budget'] });
    },
  });

  const s = summary.data;
  const registry = suppliers.data?.suppliers ?? [];
  // Per-supplier spend source switches with the period toggle (month vs today).
  const periodSpend = period === 'day' ? daily.data?.by_supplier ?? [] : s?.by_supplier ?? [];
  const spendByName = new Map(periodSpend.map((r) => [r.key, r.total_usd_cents]));

  // Join the registry with current spend; suppliers with spend but no registry
  // row (auto-detected, not yet ranged) are appended.
  const rows = registry.map((r) => ({
    ...r,
    spend: spendByName.get(r.supplier) ?? 0,
  }));
  for (const [key, total] of spendByName) {
    if (!registry.some((r) => r.supplier === key)) {
      rows.push({
        supplier: key,
        category: 'uncategorized',
        expected_min_usd_cents: 0,
        expected_max_usd_cents: 0,
        added_by: 'omega',
        first_seen: '',
        spend: total,
      });
    }
  }
  rows.sort((a, b) => b.spend - a.spend);
  const alerts = rows.filter((r) => r.expected_max_usd_cents > 0 && r.spend > r.expected_max_usd_cents);

  const pct =
    s && s.ceiling_month_usd_cents > 0
      ? Math.min(100, Math.round((s.spent_month_usd_cents / s.ceiling_month_usd_cents) * 100))
      : 0;
  const ceilingColor = STATUS_COLORS[s ? ceilingStatus(s.degradation) : 'unknown'];

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', color: '#111827' }}>
      <OmegaSidebar />
      <div
        style={{
          maxWidth: 980,
          margin: '0 auto',
          padding: isMobile ? '56px 16px 80px' : '20px 16px 80px',
          paddingLeft: isMobile ? 16 : SIDEBAR_WIDTH + 24,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Budget &amp; Suppliers</h1>
        <p style={{ margin: '4px 0 18px', color: '#6b7280', fontSize: 13 }}>
          Monthly spend per supplier against the A-02 ceiling.
        </p>

        {summary.isError && (
          <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>Failed to load budget.</div>
        )}

        {/* Total vs ceiling */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#6b7280' }}>This month</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {s ? `${fmtUsd(s.spent_month_usd_cents)} / ${s.ceiling_month_usd_cents > 0 ? fmtUsd(s.ceiling_month_usd_cents) : 'no ceiling'}` : '—'}
            </div>
          </div>
          <div style={{ marginTop: 10, height: 8, background: '#f3f4f6', borderRadius: 999 }}>
            <div style={{ width: `${pct}%`, height: 8, background: ceilingColor, borderRadius: 999 }} />
          </div>
          {s && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
              {pct}% used · {fmtUsd(Math.max(0, s.remaining_usd_cents))} remaining · status: {s.degradation}
            </div>
          )}
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 18 }}>
            <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 13, marginBottom: 4 }}>
              {alerts.length} supplier{alerts.length === 1 ? '' : 's'} over expected range
            </div>
            {alerts.map((a) => (
              <div key={a.supplier} style={{ fontSize: 13, color: '#4b5563' }}>
                {a.supplier}: {fmtUsd(a.spend)} (expected ≤ {fmtUsd(a.expected_max_usd_cents)})
              </div>
            ))}
          </div>
        )}

        {/* Supplier table */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6b7280' }}>
              Suppliers
            </span>
            <span style={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
              {(['month', 'day'] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  style={{
                    padding: '4px 12px',
                    fontSize: 12,
                    border: 'none',
                    cursor: 'pointer',
                    background: period === p ? '#111827' : '#fff',
                    color: period === p ? '#fff' : '#4b5563',
                  }}
                >
                  {p === 'month' ? 'This month' : 'Today'}
                </button>
              ))}
            </span>
          </div>
          {suppliers.isPending && <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Loading…</div>}
          {!suppliers.isPending && rows.length === 0 && (
            <div style={{ padding: 16, color: '#6b7280', fontSize: 13, fontStyle: 'italic' }}>No suppliers yet.</div>
          )}
          {rows.map((r) => {
            const st = supplierStatus(r.spend, r.expected_min_usd_cents, r.expected_max_usd_cents);
            const open = openHistory === r.supplier;
            return (
              <div key={r.supplier}>
                <button
                  type="button"
                  onClick={() => setOpenHistory(open ? null : r.supplier)}
                  style={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 16px',
                    borderTop: '1px solid #f3f4f6',
                    border: 'none',
                    borderTopWidth: 1,
                    borderTopStyle: 'solid',
                    borderTopColor: '#f3f4f6',
                    background: open ? '#fafafa' : 'transparent',
                    flexWrap: 'wrap',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_COLORS[st], flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 120, fontWeight: 600, fontSize: 13 }}>{r.supplier}</span>
                  <span style={{ fontSize: 12, color: '#9ca3af', minWidth: 90 }}>{r.category}</span>
                  <span style={{ fontSize: 13, minWidth: 80, textAlign: 'right' }}>{fmtUsd(r.spend)}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af', minWidth: 110, textAlign: 'right' }}>
                    {r.expected_max_usd_cents > 0 ? `exp ≤ ${fmtUsd(r.expected_max_usd_cents)}` : 'no range'}
                  </span>
                  <span style={{ fontSize: 10, color: '#9ca3af', width: 44, textAlign: 'right' }}>
                    {r.added_by === 'omega' ? 'auto' : 'manual'} {open ? '▴' : '▾'}
                  </span>
                </button>
                {open && <SupplierHistory supplier={r.supplier} />}
              </div>
            );
          })}
        </div>

        {/* Add supplier (manual) */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 18px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Add a supplier</div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const cents = Math.round((parseFloat(amount) || 0) * 100);
              if (name.trim()) addSupplier.mutate({ supplier: name.trim(), category: category.trim() || 'other', amount_usd_cents: cents });
            }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Supplier" style={inputStyle} />
            <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category" style={inputStyle} />
            <input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Expected $/mo" inputMode="decimal" style={{ ...inputStyle, width: 120 }} />
            <button type="submit" disabled={addSupplier.isPending || !name.trim()} style={btnStyle}>
              {addSupplier.isPending ? 'Adding…' : 'Add'}
            </button>
          </form>
          {addSupplier.isError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>Could not add supplier.</div>}
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
            New expenses from unknown suppliers are auto-detected and registered by OMEGA.
          </div>
        </div>
      </div>
    </div>
  );
}

/** Inline 30-day spend trend for one supplier (mini bar chart). */
function SupplierHistory({ supplier }: { supplier: string }) {
  const history = useQuery({
    queryKey: ['budget', 'history', supplier],
    queryFn: () =>
      apiFetch<{ supplier: string; days: DayCents[] }>(
        `/admin/budget/supplier-history/${encodeURIComponent(supplier)}`,
      ),
  });
  if (history.isPending) {
    return <div style={{ padding: '8px 16px', fontSize: 12, color: '#9ca3af' }}>Loading trend…</div>;
  }
  if (history.isError) {
    return <div style={{ padding: '8px 16px', fontSize: 12, color: '#dc2626' }}>Trend unavailable.</div>;
  }
  const days = history.data?.days ?? [];
  if (days.length === 0) {
    return <div style={{ padding: '8px 16px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No spend in the last 30 days.</div>;
  }
  const max = Math.max(...days.map((d) => d.total_usd_cents), 1);
  return (
    <div style={{ padding: '10px 16px 14px', background: '#fafafa' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 48 }}>
        {days.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${fmtUsd(d.total_usd_cents)}`}
            style={{
              flex: 1,
              minWidth: 2,
              height: `${Math.max(2, Math.round((d.total_usd_cents / max) * 48))}px`,
              background: '#2563eb',
              borderRadius: 1,
            }}
          />
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
        Last 30 days · peak {fmtUsd(max)}/day
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 120,
  padding: '7px 10px',
  fontSize: 13,
  border: '1px solid #e5e7eb',
  borderRadius: 6,
};

const btnStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  background: '#111827',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
};
